import { importLibrary } from '@googlemaps/js-api-loader';
import Backbone from 'backbone';
import $ from 'jquery';
import moment from 'moment';
import _ from 'underscore';
import templateInfoWindow from '../templates/partial.property_info_window.html';
import templateListingItem from '../templates/partial.property_listing.html';
import templateSearchExpired from '../templates/partial.search.expired.html';
import templateNoResults from '../templates/partial.search.no_results.html';
import { getFormData, putFormData } from './form-helper';
import {
    addressText,
    bathroomIcon,
    bedroomNo,
    buildingUrl,
    cardTitleUrl,
    getCost,
    getPhotoUrl,
    getSize,
    isMembersOnly,
    moneyFormat,
    propertyAlias,
    structureDetailsUrl,
    structureTypeText,
    t,
    trainInfo,
    unitDetailsUrl,
} from './helper';

const criteriaMap = {
    'p[l]': 'price[low]',
    'p[h]': 'price[high]',
    df: 'date_from',
    dt: 'date_to',
    st: 'structure_type',
    'sz[l]': 'size[low]',
    'sz[h]': 'size[high]',
    'st_sz[l]': 'structure_size[low]',
    'st_sz[h]': 'structure_size[high]',
    bed: 'bedroom_no',
    new: 'new_development',
    pet: 'pet_friendly',
    pk: 'parking_type',
    ff: 'fully_furnished',
    nkm: 'no_key_money',
    hr: 'high_rise',
    reno: 'renovated',
    v: 'vintage',
    lb: 'large_balcony',
    cnr: 'corner_room',
    name: 'structure_name',
    key: 'hash_id',
};

/**
 * Map fields to API parameters
 * @param {object} criteria
 * @returns {object}
 */
function mapFieldsToApiParams(criteria) {
    const mappedCriteria = {};

    // Loop through criteria
    _.each(criteria, (value, key) => {
        // If key has entry in criteria map, use it
        if (criteriaMap[key]) {
            mappedCriteria[criteriaMap[key]] = value;
        }
        // Else use key as-is
        else {
            mappedCriteria[key] = value;
        }
    });

    return mappedCriteria;
}

function buildQuery(criteria, options) {
    // Create query by extending criteria, sort, and options
    const query = _.extend({}, mapFieldsToApiParams(criteria), options);

    // If date_from and date_to specified; calculate nights_stay
    if (query.date_from && query.date_to) {
        query.nights_stay = moment(query.date_to).diff(
            moment(query.date_from),
            'days'
        );
    }

    if (query.bedroom_no) {
        // If not array, convert to array
        if (!_.isArray(query.bedroom_no)) {
            query.bedroom_no = [query.bedroom_no];
        }

        // If "4" chosen, add "5" to "10"
        if (-1 !== query.bedroom_no.indexOf(4)) {
            query.bedroom_no = _.union(query.bedroom_no, [5, 6, 7, 8, 9, 10]);
        }
    }

    // Extend defaults and return
    return _.extend(
        {
            sort_key: 'updated_at',
            sort_order: 'desc',
            limit: HJ.Config.resultsLimit,
            offset: 0,
        },
        query
    );
}

export default class PropertySearchView extends Backbone.View {
    preinitialize(options) {
        // Set "API client ID"
        this.api_client_id = options.api_client_id || null;
        if (this.api_client_id === null) {
            throw Error('Parameter api_client_id not set');
        }

        // Set "section"
        this.section = 'buy';

        // Set search key
        this.key = options.key;

        this.locale = options.locale;

        this.Markers = {};

        Promise.all([importLibrary('core'), importLibrary('maps')]).then(
            ([CoreLibrary, MapsLibrary]) => {
                // Initialize map canvas
                this.MapCanvas = new MapsLibrary.Map(
                    document.getElementById('map-canvas'),
                    {
                        mapId: '7812172cc40544ecc082ee2a', // Styling controlled via Google Cloud Console
                        center: new CoreLibrary.LatLng(36.6982, 137.8619),
                        zoom: 12,
                        minZoom: 5,
                        scaleControl: true,
                        cameraControl: false,
                        zoomControl: true,
                        scrollWheel: true,
                        streetViewControl: false,
                        clickableLabels: false,
                        mapTypeControl: false,
                        gestureHandling: 'greedy',
                    }
                );

                /** For some reason we can't directly pass the close() function to addListener() */
                this.MapCanvas.addListener('click', () => {
                    this.infoWindow.close();
                });

                // Create marker bounds object for use in Google maps
                this.MarkerBounds = new CoreLibrary.LatLngBounds();

                this.clickedMarkers = localStorage.getItem('clickedMarkers')
                    ? JSON.parse(localStorage.getItem('clickedMarkers'))
                    : [];

                // Initialize info window
                this.infoWindow = new MapsLibrary.InfoWindow({
                    maxWidth: 350,
                });
            }
        );

        // Set search state properties
        this.lastSearchId = undefined;

        this.events = {
            'click .toggle-search-list-map button':
                this.handleClickSearchListMap,
            'submit .search-criteria form': this.handleSubmit,
            'change .sort-menu form select': this.handleChangeSort,
            'mouseenter .property-listing': this.highlightMap,
            'mouseleave .property-listing': this.highlightMap,
            'click .dropdown': this.handleCriteriaClick,
            'click #criteria-toggle': this.handleCriteriaToggle,
            'click #criteria-clear': this.handleCriteriaClear,
            'change .criteria__filter': this.search,
            'change .criteria-dropdown': this.setCriteriaLabels,
            'change input[name="bed"]': this.setBedroomInputValues,
        };

        // Hide footer (page has infinite scroll)
        $('footer').hide();
    }

    initialize() {
        // Initialize spinner
        this.Spinner = this.$el.find('.spinner');
        this.Spinner.hide();

        // Initialize property list
        this.propertyList = this.$el.find('.property-listings');
        this.propertyList.empty();

        // Get search criteria
        const urlParams = new URLSearchParams(
            window.location.hash.substring(1)
        );
        const previousCriteria = JSON.parse(urlParams.get('c')) || {};
        const previousSortKey = urlParams.get('sk');
        const previousSortOrder = urlParams.get('so');

        this.currentCriteria = {};
        this.criteriaElOpen = false;

        // Prepopulate search filters with saved data
        if (_.isEmpty(this.key)) {
            if (_.size(previousCriteria) > 0) {
                // Put form data
                putFormData(
                    this.$el.find('.search-criteria form'),
                    previousCriteria,
                    false
                );

                this.setCriteriaLabels();
            }

            // Set sort key and order
            if (previousSortKey && previousSortOrder) {
                this.el.querySelector(
                    '.sort-menu form [name="sort_criteria"]'
                ).value = `${previousSortKey}_${previousSortOrder}`;
            } else {
                this.el.querySelector(
                    '.sort-menu form [name="sort_criteria"]'
                ).value = 'updated_at_desc';
            }
        }

        // Toggle list mode as default to highlight properties
        this.toggleSearchListMap('list');

        // Check active button on window resize and simulate click on "list" button if in "search" mode
        $(window).on('resize', this.checkSearchListMapMode);

        // Google Analytics Client ID
        const ga_client_id = document.cookie.match(/_ga=(.+?);/);
        if (ga_client_id) {
            this.gaClientID = ga_client_id[1].split('.').slice(-2).join('.');
        }

        // Search properties
        this.search();
    }

    handleClickSearchListMap($e) {
        // Toggle search/list/map based on button value
        this.toggleSearchListMap($e.currentTarget.value);
    }

    /**
     * @param {string} mode
     */
    toggleSearchListMap(mode) {
        const searchCriteria = this.$el.find('.search-criteria');
        const searchListings = this.$el.find('.search-listings');
        const searchMap = this.$el.find('.search-map');

        // Remove class "active" from all buttons
        this.$el.find('.toggle-search-list-map button').removeClass('active');

        // Add class "active" to desired
        this.$el.find('button[value="' + mode + '"]').addClass('active');

        // If "search" mode
        if (mode === 'search') {
            // Hide listings and sidebar
            searchListings.addClass('d-none');
            searchMap.addClass('d-none');

            // Show criteria
            searchCriteria.removeClass('d-none');
        }
        // Else If "map" mode
        else if (mode === 'map') {
            // Hide criteria and listings
            searchCriteria.addClass('d-none');
            searchListings.addClass('d-none');

            // Show map
            searchMap.removeClass('d-none');

            // Recenter map now that it's visible
            this.recenterMap();
        }
        // Else if "list" mode
        else if (mode === 'list') {
            // Hide criteria and map
            searchCriteria.addClass('d-none');
            searchMap.addClass('d-none');

            // Show listings
            searchListings.removeClass('d-none');
        }
    }

    recenterMap() {
        // Fit map to bounds of all markers
        if (!this.MarkerBounds.isEmpty()) {
            this.MapCanvas.fitBounds(this.MarkerBounds);
        }
    }

    checkSearchListMapMode() {
        const searchSelector = document.querySelector('.toggle-search-list-map')
            .children[0];
        const listSelector = document.querySelector('[value="list"]');

        // If currently in search mode and new window width is >=768px (tablet and above)
        if (
            searchSelector.classList.contains('active') &&
            window.innerWidth >= 768
        ) {
            // Simulate click on "list" mode (otherwise page remains in search mode, which is not available for tablet width and above)
            listSelector.click();
        }
    }

    handleCriteriaToggle() {
        const criteriaDiv = document.getElementById('hidden-criteria');
        const criteriaButton = document.getElementById('criteria-toggle');

        const toggleClasses = [
            'd-md-none',
            'd-md-flex',
            'flex-wrap',
            'justify-content-center',
        ];

        toggleClasses.map((classToToggle) =>
            criteriaDiv.classList.toggle(classToToggle)
        );

        criteriaButton.innerText = criteriaDiv.classList.contains('d-md-none')
            ? t('more_criteria')
            : t('hide_criteria');

        this.criteriaElOpen = !this.criteriaElOpen;
    }

    handleCriteriaClear() {
        const criteriaForm = this.$el.find('.search-criteria form')[0];

        criteriaForm.reset();

        this.setCriteriaLabels();
        this.search();
    }

    handleChangeSort() {
        // Scroll to top
        $(window).scrollTop(0);

        // Search properties
        this.search();
    }

    async handleSubmit($e) {
        $e.preventDefault();

        // Scroll to top
        $(window).scrollTop(0);

        // Switch to map mode
        this.toggleSearchListMap('list');

        // Close criteria div if open
        const criteriaButton = document.getElementById('criteria-toggle');
        if (this.criteriaElOpen) {
            criteriaButton.click();
        }

        // Record search
        window.dataLayer &&
            window.dataLayer.push({
                event: 'search',
                event_category: 'Searches',
                event_label: this.section + ' search',
            });
    }

    async search() {
        // Clear property list & map markers
        this.propertyList.empty();
        this.clearAllMarkers();

        // Close any info windows
        if (this.infoWindow) {
            this.infoWindow.close();
        }

        // Get form data
        const currentCriteria = getFormData(
            this.$el.find('.search-criteria form')
        );

        // Reduce criteria added from multiples of same input
        for (let [key, value] of Object.entries(currentCriteria)) {
            if (Array.isArray(value) && value.length > 0) {
                const resetValue = value.filter(
                    (value, index, array) => array.indexOf(value) === index
                );

                if (resetValue.length > 1) {
                    currentCriteria[key] = resetValue;
                } else {
                    currentCriteria[key] = resetValue[0];
                }
            }
        }

        const sortCriteria = this.el.querySelector(
            '.sort-menu form [name="sort_criteria"]'
        ).value;

        const { currentSortKey, currentSortOrder } =
            this.setSortCriteria(sortCriteria);

        this.currentCriteria = currentCriteria;

        // Store search criteria
        const newUrl =
            window.location.protocol +
            '//' +
            window.location.host +
            window.location.pathname +
            '#c=' +
            JSON.stringify(currentCriteria) +
            '&sk=' +
            currentSortKey +
            '&so=' +
            currentSortOrder;
        window.history.pushState({ path: newUrl }, '', newUrl);

        const options = {};
        options.property_type = 'sales';
        options.campaign = 'resorts_hakuba';
        options.resort = 1;
        options.sort_key = currentSortKey;
        options.sort_order = currentSortOrder;
        options.api_client_id = this.api_client_id;
        options.hash_id = this.key;
        options.ga_client_id = this.gaClientID;

        // Convert into query
        this.query = buildQuery(currentCriteria, options);

        // Set language parameter
        this.query.language = HJ.Config.language;

        // Show spinner
        this.Spinner.show();

        // Load search results
        const response = await fetch(
            HJ.Config.api_url + '/search?' + $.param(this.query),
            {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
            }
        );

        // Hide spinner
        this.Spinner.hide();

        if (!response.ok) {
            if (response.status === 410) {
                // Search has expired
                this.propertyList.html(
                    templateSearchExpired({
                        t,
                    })
                );
                return;
            }

            const responseData = await response.json();

            // Create and throw error
            const e = new Error(responseData.exception[0].message);
            e.name = responseData.exception[0].type;
            throw e;
        }

        const responseData = await response.json();

        this.lastSearchId = responseData.search_id;
        sessionStorage.setItem('lastSearchId', responseData.search_id);

        // If no results found
        if (
            this.query.offset === 0 &&
            typeof responseData.results !== 'undefined' &&
            responseData.results.length === 0
        ) {
            this.propertyList.html(
                templateNoResults({
                    t: t,
                })
            );
        }
        // Else results
        else {
            /**
             * Append items
             */
            const structures = responseData.results;

            let items = '';

            Promise.all([importLibrary('core'), importLibrary('marker')]).then(
                ([CoreLibrary, MarkerLibrary]) => {
                    _.each(structures, (structure) => {
                        // Determine property title
                        let title;
                        if (isMembersOnly(structure)) {
                            title = propertyAlias(structure);
                        } else if (structure.x_tagline) {
                            title = structure.x_tagline;
                        } else {
                            title = structure.x_structure_name;
                        }

                        // If no marker for current location, and latitude and longitude are valid
                        if (
                            !(structure.location.id in this.Markers) &&
                            Number(structure.location.longitude) > 0 &&
                            Number(structure.location.latitude) > 0
                        ) {
                            const previouslyClicked =
                                this.clickedMarkers.includes(
                                    structure.location.id
                                );

                            const iconUrl = previouslyClicked
                                ? HJ.Config.icons.url_base_over + '/dot.png'
                                : HJ.Config.icons.url_base_out + '/dot.png';

                            const rJDot = document.createElement('img');
                            rJDot.src = iconUrl;
                            rJDot.width = 25;
                            rJDot.height = 25;

                            // Create advanced marker element
                            const marker =
                                new MarkerLibrary.AdvancedMarkerElement({
                                    map: this.MapCanvas,
                                    position: new CoreLibrary.LatLng(
                                        structure.location.latitude,
                                        structure.location.longitude
                                    ),
                                    title: title,
                                    content: rJDot,
                                });

                            // If user has specified search criteria
                            if (
                                Object.keys(this.currentCriteria).length !== 0
                            ) {
                                // Extend the bounds by the marker
                                this.MarkerBounds.extend(marker.position);
                            }
                            // Else, no criteria (default view)
                            else {
                                this.MarkerBounds.extend(marker.position);
                            }

                            // Add click listener
                            marker.addListener('click', () => {
                                // If window >= desktop width
                                if (window.innerWidth >= 992) {
                                    // Scroll to property card in search results
                                    if (
                                        $(
                                            '[data-location="' +
                                                structure.location.id +
                                                '"]'
                                        ).length
                                    ) {
                                        const el = $(
                                            '[data-location="' +
                                                structure.location.id +
                                                '"]'
                                        );

                                        const prevEl =
                                            this.el.querySelector(
                                                '.clicked-property'
                                            );

                                        if (prevEl) {
                                            prevEl.classList.remove(
                                                'clicked-property'
                                            );
                                        }

                                        const listingsEl =
                                            $('.search-listings');
                                        const elOffsetTop = el.position().top;
                                        const navHeight = $('.navbar').height();

                                        listingsEl.scrollTop(
                                            listingsEl.scrollTop() +
                                                elOffsetTop -
                                                navHeight
                                        );

                                        el.addClass('clicked-property');
                                    }
                                } else {
                                    // Display info window
                                    const content = templateInfoWindow({
                                        language: HJ.Config.language,
                                        badge: 'badge-for-sale',
                                        coverphoto: getPhotoUrl(structure),
                                        title: title,
                                        key: this.key,
                                        section: this.section,
                                        structure_type:
                                            structureTypeText(structure),
                                        address: addressText(structure),
                                        isMembersOnly: isMembersOnly(structure),
                                        buildingUrl,
                                        structureDetailsUrl,
                                        unitDetailsUrl,
                                        cardTitleUrl: cardTitleUrl(
                                            structure,
                                            this.section,
                                            this.key
                                        ),
                                        getCost,
                                        getSize,
                                        bathrooms: bathroomIcon,
                                        bedroomNo,
                                        moneyFormat,
                                        structure,
                                        t,
                                    });
                                    this.infoWindow.setContent(content);
                                    this.infoWindow.setPosition(
                                        marker.position
                                    );
                                    this.infoWindow.open(
                                        this.MapCanvas,
                                        marker
                                    );
                                }

                                if (
                                    !this.clickedMarkers.includes(
                                        structure.location.id
                                    )
                                ) {
                                    this.clickedMarkers.push(
                                        structure.location.id
                                    );

                                    localStorage.setItem(
                                        'clickedMarkers',
                                        JSON.stringify(this.clickedMarkers)
                                    );

                                    marker.content.src =
                                        HJ.Config.icons.url_base_over +
                                        '/dot.png';
                                    $(
                                        '[data-location="' +
                                            structure.location.id +
                                            '"]'
                                    ).addClass('mapIconOver');
                                }
                            });

                            // Add mouseover listener
                            marker.addListener('mouseover', function () {
                                marker.content.src =
                                    HJ.Config.icons.url_base_over + '/dot.png';
                                $(
                                    '[data-location="' +
                                        structure.location.id +
                                        '"]'
                                ).addClass('mapIconOver');
                            });

                            // Add mouseout listener
                            marker.addListener('mouseout', function () {
                                const storedMarkers = localStorage.getItem(
                                    'clickedMarkers'
                                )
                                    ? JSON.parse(
                                          localStorage.getItem('clickedMarkers')
                                      )
                                    : [];

                                if (
                                    !storedMarkers.includes(
                                        structure.location.id
                                    )
                                ) {
                                    marker.content.src =
                                        HJ.Config.icons.url_base_out +
                                        '/dot.png';
                                    $(
                                        '[data-location="' +
                                            structure.location.id +
                                            '"]'
                                    ).removeClass('mapIconOver');
                                }
                            });

                            // Add marker to list
                            this.Markers[structure.location.id] = marker;
                        }

                        items += templateListingItem({
                            language: HJ.Config.language,
                            featured: structure.featured,
                            coverphoto: getPhotoUrl(structure),
                            title,
                            key: this.key,
                            section: this.section,
                            structure_type: structureTypeText(structure),
                            address: addressText(structure),
                            isMembersOnly: isMembersOnly(structure),
                            buildingUrl,
                            structureDetailsUrl,
                            unitDetailsUrl,
                            cardTitleUrl: cardTitleUrl(
                                structure,
                                this.section,
                                this.key
                            ),
                            trainInfo,
                            getCost,
                            getSize,
                            bathroomIcon,
                            bedroomNo,
                            moneyFormat,
                            structure,
                            t,
                        });
                    });

                    // Append items to property list
                    this.propertyList.append(items);

                    this.setCriteriaLabels();

                    // Recenter map
                    this.recenterMap();
                }
            );
        }
    }

    clearAllMarkers() {
        // Remove all markers from map
        Object.entries(this.Markers).forEach(([, marker]) =>
            marker.setMap(null)
        );

        // Reset markers list
        this.Markers = {};

        importLibrary('core').then((CoreLibrary) => {
            this.MarkerBounds = new CoreLibrary.LatLngBounds();
        });
    }

    highlightMap($e) {
        const location = $e.currentTarget.dataset.location;
        // const structure_type = $e.currentTarget.dataset.structureType;
        let type = '';

        const storedMarkers = localStorage.getItem('clickedMarkers')
            ? JSON.parse(localStorage.getItem('clickedMarkers'))
            : [];

        if (!storedMarkers.includes(Number(location))) {
            type = $e.type === 'mouseenter' ? 'over' : 'out';
        } else {
            type = 'over';
        }

        const marker = this.Markers[location];

        if ('over' === type) {
            marker.zIndex = 99;
        } else {
            marker.zIndex = 0;
        }

        const icon_url = HJ.Config.icons['url_base_' + type] + '/dot.png';

        if (
            marker.content.src != icon_url &&
            !storedMarkers.includes(Number(location))
        ) {
            marker.content.src = icon_url;
        }
    }

    setSortCriteria(sortString) {
        const stringElements = sortString.split('_');

        let currentSortKey;
        const currentSortOrder = stringElements.pop();

        if (stringElements.length > 1) {
            currentSortKey = stringElements.join('_');
        } else {
            currentSortKey = stringElements[0];
        }

        return { currentSortKey, currentSortOrder };
    }

    handleCriteriaClick($e) {
        const dropdownToggle = $e.target;
        const searchCriteriaInputs = dropdownToggle.nextElementSibling;

        searchCriteriaInputs.classList.toggle('d-none');

        const handleInputGroupClick = (event) => {
            const clickedElement = event.target;

            if (!searchCriteriaInputs.contains(clickedElement)) {
                if (clickedElement !== dropdownToggle) {
                    searchCriteriaInputs.classList.add('d-none');
                    this.el.removeEventListener('click', handleInputGroupClick);
                }
            }
        };

        this.el.addEventListener('click', handleInputGroupClick);
    }

    setCriteriaLabels(e) {
        this.setPriceCriteriaLabel(e);
        this.setSizeCriteriaLabel(e);
    }

    setPriceCriteriaLabel(e) {
        console.debug('PropertySearchView#SetPriceCriteriaLabel');

        const allPriceMinInputs = this.el.querySelectorAll('[name="p[l]"]');
        const allPriceMaxInputs = this.el.querySelectorAll('[name="p[h]"]');
        const priceRangeLabel = this.el.querySelector('#price-range');

        // Price
        let { minValue } = this.setPriceInputValues(
            e,
            'p[l]',
            allPriceMinInputs
        );
        let { maxValue } = this.setPriceInputValues(
            e,
            'p[h]',
            allPriceMaxInputs
        );

        priceRangeLabel.innerText = this.setPriceInputLabels(
            minValue,
            maxValue
        );
    }

    setPriceInputLabels(minValue, maxValue) {
        const numberStringFormatter = (value) => {
            return Number(value).toLocaleString('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
            });
        };

        const DEFAULT_LABEL = this.locale === 'ja' ? '価格' : 'Any Price';
        let displayValue = '';
        let label = '';

        if (minValue && !maxValue) {
            displayValue = numberStringFormatter(minValue);

            label = `¥${displayValue}+`;
        } else if (maxValue && !minValue) {
            displayValue = numberStringFormatter(maxValue);

            label = `Up to ¥${displayValue}`;
        } else if (minValue && maxValue) {
            const minDisplayValue = numberStringFormatter(minValue);
            const maxDisplayValue = numberStringFormatter(maxValue);

            label = `¥${minDisplayValue} - ¥${maxDisplayValue}`;
        } else {
            label = DEFAULT_LABEL;
        }

        return label;
    }

    setPriceInputValues(event, inputName, inputsArray) {
        const INPUT_NAMES = {
            priceLow: 'p[l]',
            priceHigh: 'p[h]',
        };

        let minValue, maxValue;

        if (inputName === INPUT_NAMES.priceLow) {
            if (event && event.target.name === INPUT_NAMES.priceLow) {
                minValue = Number(event.target.value);

                inputsArray.forEach((input) => {
                    input.value = minValue > 0 ? minValue : '';
                });
            } else {
                minValue = Number(
                    this.el.querySelector(`[name="${INPUT_NAMES.priceLow}"]`)
                        .value
                );
            }
        } else if (inputName === INPUT_NAMES.priceHigh) {
            if (event && event.target.name === INPUT_NAMES.priceHigh) {
                maxValue = Number(event.target.value);

                inputsArray.forEach((input) => {
                    input.value = maxValue > 0 ? maxValue : '';
                });
            } else {
                maxValue = Number(
                    this.el.querySelector(`[name="${INPUT_NAMES.priceHigh}"]`)
                        .value
                );
            }
        }

        return { minValue, maxValue };
    }

    setSizeCriteriaLabel(e) {
        console.debug('PropertySearchView#setSizeCriteriaLabel');

        const allSizeMinInputs = this.el.querySelectorAll('[name="sz[l]"]');
        const allSizeMaxInputs = this.el.querySelectorAll('[name="sz[h]"]');

        const allStructureSizeMinInputs =
            this.el.querySelectorAll('[name="st_sz[l]"]');
        const allStructureSizeMaxInputs =
            this.el.querySelectorAll('[name="st_sz[h]"]');

        const sizeRangeLabel = this.el.querySelector('#size-range');

        if (allSizeMinInputs.length > 0 && allSizeMaxInputs.length > 0) {
            const isUnitSize = !!this.el
                .querySelector('#criteria-bar__size')
                .querySelector('select[name="sz[l]"]');

            if (isUnitSize) {
                let { minValue } = this.setSizeInputValues(
                    e,
                    'sz[l]',
                    allSizeMinInputs
                );
                let { maxValue } = this.setSizeInputValues(
                    e,
                    'sz[h]',
                    allSizeMaxInputs
                );

                sizeRangeLabel.innerText = this.setSizeInputLabels(
                    minValue,
                    maxValue
                );
            }
        }

        if (
            allStructureSizeMinInputs.length > 0 &&
            allStructureSizeMaxInputs.length > 0
        ) {
            const isStructureSize = !!this.el
                .querySelector('#criteria-bar__size')
                .querySelector('select[name="st_sz[l]"]');

            if (isStructureSize) {
                let { minValue } = this.setSizeInputValues(
                    e,
                    'st_sz[l]',
                    allStructureSizeMinInputs
                );
                let { maxValue } = this.setSizeInputValues(
                    e,
                    'st_sz[h]',
                    allStructureSizeMaxInputs
                );

                sizeRangeLabel.innerText = this.setSizeInputLabels(
                    minValue,
                    maxValue
                );
            }
        }
    }

    setSizeInputLabels(minValue, maxValue) {
        const DEFAULT_LABEL = this.locale === 'ja' ? '広さ' : 'Any Size';
        let label = '';

        if (minValue && !maxValue) {
            label = `${minValue}m²+`;
        } else if (maxValue && !minValue) {
            label = `Up to ${maxValue}m²`;
        } else if (minValue && maxValue) {
            label = `${minValue}m² - ${maxValue}m²`;
        } else {
            label = DEFAULT_LABEL;
        }

        return label;
    }

    setSizeInputValues(event, inputName, inputsArray) {
        const INPUT_NAMES = {
            sizeLow: 'sz[l]',
            sizeHigh: 'sz[h]',
            structureSizeLow: 'st_sz[l]',
            structureSizeHigh: 'st_sz[h]',
        };

        let minValue, maxValue;

        if (inputName === INPUT_NAMES.sizeLow) {
            if (event && event.target.name === INPUT_NAMES.sizeLow) {
                minValue = Number(event.target.value);

                inputsArray.forEach((input) => {
                    input.value = minValue > 0 ? minValue : '';
                });
            } else {
                minValue = Number(
                    this.el.querySelector(`[name="${INPUT_NAMES.sizeLow}"]`)
                        .value
                );
            }
        } else if (inputName === INPUT_NAMES.sizeHigh) {
            if (event && event.target.name === INPUT_NAMES.sizeHigh) {
                maxValue = Number(event.target.value);

                inputsArray.forEach((input) => {
                    input.value = maxValue > 0 ? maxValue : '';
                });
            } else {
                maxValue = Number(
                    this.el.querySelector(`[name="${INPUT_NAMES.sizeHigh}"]`)
                        .value
                );
            }
        }

        return { minValue, maxValue };
    }

    setBedroomInputValues(e) {
        const bedroomInputs = Array.from(
            document.querySelectorAll("input[name='bed']")
        ).filter((input) => input !== e.target);

        bedroomInputs.forEach((input) => {
            if (input.value === e.target.value) {
                input.checked = e.target.checked;
            }
        });
    }
}
